import { inject, Injectable } from '@angular/core';
import { BrandService } from 'src/app/core/services/brand.service';

/** Product module the login flow was entered for. */
export type AuthModule = 'tx' | 'business';
/** Authentication method shown on the login screens. */
export type AuthMethod = 'email' | 'sms';

const STORAGE_KEY = 'auth.module';

/** Method the toggle is seeded to when a module is entered. User can flip it after. */
const DEFAULT_METHOD: Record<AuthModule, AuthMethod> = {
  tx: 'email',
  business: 'sms',
};

/**
 * Resolves and persists the `?module=` login context.
 *
 * - `sessionStorage` holds the active module for the current tab session.
 * - `localStorage` holds the module across the magic-link round-trip (the
 *   email callback opens in a fresh context where `sessionStorage` is empty).
 * - When no explicit module is provided (no query param, no stored value),
 *   the default tracks `BrandService` — `tx.*` hosts default to `tx`, every
 *   other host (business.*, localhost, white-label domains) defaults to
 *   `business`. `BrandService.initializeBrand` runs as an APP_INITIALIZER
 *   (see `app.config.ts`), so `isTxMode()` is already settled by the time
 *   any login screen renders.
 */
@Injectable({ providedIn: 'root' })
export class AuthModuleService {
  private brand = inject(BrandService);

  /**
   * Coerce any raw query/storage value to a known module.
   * - `'business'` or `'tx'` are honored explicitly.
   * - Unknown/missing values delegate to `BrandService.isTxMode()` — single
   *   source of truth for the TX vs non-TX brand split.
   */
  resolveModule(raw: string | null | undefined): AuthModule {
    if (raw === 'business') return 'business';
    if (raw === 'tx') return 'tx';
    return this.brand.isTxMode() ? 'tx' : 'business';
  }

  /** Persist the active module for the current tab session. */
  setModule(module: AuthModule): void {
    sessionStorage.setItem(STORAGE_KEY, module);
  }

  /** Read the active module for the current tab session. */
  getModule(): AuthModule {
    return this.resolveModule(sessionStorage.getItem(STORAGE_KEY));
  }

  /** The method the toggle should be seeded to for a given module. */
  defaultMethodFor(module: AuthModule): AuthMethod {
    return DEFAULT_METHOD[module];
  }

  /** Stash the module so the magic-link callback can recover it post-redirect. */
  rememberForMagicLink(module: AuthModule): void {
    localStorage.setItem(STORAGE_KEY, module);
  }

  /** Recover the module stashed before a magic-link send. */
  recallFromMagicLink(): AuthModule {
    return this.resolveModule(localStorage.getItem(STORAGE_KEY));
  }
}
